// Story 2.10 — /account/buy/:dropId
//
// The SPA route the Astro public-site's DossierBuyButton (Story 2.8)
// deep-links to. Pure orchestrator: fetches the drop summary + the wallet
// balance, mounts <PurchaseConfirmModal /> open-by-default, mints idempotency
// keys per submit, handles the top-up interrupt + resume flow.
//
// State machine maps the backend's branched 4xx codes onto modal states.
// On unauthenticated 401 → redirects to /login?return_to=<current path>.
// On success → invalidates wallet queries (handled by the mutation hook).
//
// No auth guard (follows the wallet/refund/gdpr precedent — let the
// backend 401 surface to the handler).

import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import toast from "react-hot-toast";

import { formatIssueNumber } from "@greenhold/components";

import { attachRequestIdToScope } from "../lib/api-client";
import { TopUpModal } from "../features/top-up/TopUpModal";
import {
  PurchaseConfirmModal,
  type PurchaseConfirmModalStatus,
} from "../features/dossier-purchase/PurchaseConfirmModal";
import {
  MAX_PURCHASE_QUANTITY,
  useDossierPurchase,
  type DossierPurchaseError,
  type PurchasedDossierResponseData,
} from "../features/dossier-purchase/useDossierPurchase";
import { useDropForPurchase } from "../features/dossier-purchase/useDropForPurchase";

type BalanceSnapshot = {
  balance_cents: number;
  escrow_cents: number;
  currency: "EUR";
};

async function fetchBalance(): Promise<BalanceSnapshot> {
  const response = await fetch("/api/v1/wallet/balance", {
    credentials: "include",
  });
  attachRequestIdToScope(response);
  if (!response.ok) {
    if (response.status === 401) {
      throw { status: 401 };
    }
    throw new Error(`balance fetch failed: ${response.status}`);
  }
  return (await response.json()) as BalanceSnapshot;
}

export function BuyDossierPage() {
  const { dropId } = useParams<{ dropId: string }>();
  const navigate = useNavigate();

  const drop = useDropForPurchase(dropId ?? "");
  const balance = useQuery({
    queryKey: ["wallet-balance"],
    queryFn: fetchBalance,
    staleTime: 30_000,
    gcTime: 5 * 60_000,
    retry: false,
  });

  const [confirmOpen, setConfirmOpen] = useState(true);
  const [quantity, setQuantity] = useState(1);
  const [topupOpen, setTopupOpen] = useState(false);
  const [idempotencyKey, setIdempotencyKey] = useState<string>(() =>
    crypto.randomUUID(),
  );
  const [status, setStatus] = useState<PurchaseConfirmModalStatus>("idle");
  const [purchaseResult, setPurchaseResult] =
    useState<PurchasedDossierResponseData | null>(null);
  const [lastError, setLastError] = useState<DossierPurchaseError | null>(null);

  const purchaseMutation = useDossierPurchase(dropId ?? "");

  // Redirect to login if the balance fetch 401s (unauthenticated).
  useEffect(() => {
    const err = balance.error as { status?: number } | null;
    if (err?.status === 401) {
      const returnTo = encodeURIComponent(window.location.pathname);
      navigate(`/login?return_to=${returnTo}`, { replace: true });
    }
  }, [balance.error, navigate]);

  const priceCents = drop.data?.price_cents ?? 0;
  const balanceCents = balance.data?.balance_cents ?? null;
  // Bound the stepper by the per-user cap when the drop carries one; the
  // backend cap check (owned + quantity ≤ cap) stays authoritative.
  const maxQuantity = Math.max(
    1,
    Math.min(MAX_PURCHASE_QUANTITY, drop.data?.per_user_cap ?? MAX_PURCHASE_QUANTITY),
  );
  const totalPriceCents = priceCents * quantity;
  const shortfallCents =
    balanceCents !== null && balanceCents < totalPriceCents
      ? totalPriceCents - balanceCents
      : undefined;

  // When the drop is already in a closed state per the GET response, jump
  // straight to drop_closed without making the user click Buy first.
  useEffect(() => {
    if (!drop.data || status !== "idle") return;
    if (drop.data.is_perpetual) {
      setStatus("drop_closed");
      return;
    }
    if (drop.data.status !== "live" && drop.data.status !== "retiring") {
      setStatus("drop_closed");
    }
  }, [drop.data, status]);

  const itemSubtitle = useMemo(() => {
    if (!drop.data) return undefined;
    const kindCopy =
      drop.data.kind === "special"
        ? "Special drop"
        : drop.data.kind === "regular"
          ? "Regular drop"
          : "Welcome drop";
    return `${kindCopy} · ${drop.data.creator_count} creator${drop.data.creator_count === 1 ? "" : "s"}`;
  }, [drop.data]);

  function handleConfirm() {
    if (!dropId) return;
    setStatus("submitting");
    setLastError(null);
    purchaseMutation.mutate(
      { idempotencyKey, quantity },
      {
        onSuccess: (data) => {
          setPurchaseResult(data);
          setStatus("succeeded");
          const minted = data.dossiers.map((d) => d.edition_number);
          toast.success(
            minted.length === 1
              ? `Issue #${formatIssueNumber(minted[0])} minted to your collection.`
              : `${minted.length} issues (#${formatIssueNumber(minted[0])}–#${formatIssueNumber(minted[minted.length - 1])}) minted to your collection.`,
          );
        },
        onError: (err) => {
          setLastError(err);
          if (err.code === "UNAUTHORIZED") {
            const returnTo = encodeURIComponent(window.location.pathname);
            navigate(`/login?return_to=${returnTo}`, { replace: true });
            return;
          }
          if (err.code === "INSUFFICIENT_BALANCE") {
            // Top-up interrupt — close confirm, open top-up.
            setStatus("idle");
            setConfirmOpen(false);
            setTopupOpen(true);
            return;
          }
          if (err.code === "DOSSIER_PURCHASE_CAP_REACHED") {
            setStatus("cap_reached");
            return;
          }
          if (err.code === "DOSSIER_PURCHASE_DROP_CLOSED") {
            setStatus("drop_closed");
            return;
          }
          if (err.code === "IDEMPOTENCY_CONFLICT") {
            // The original request already succeeded server-side OR the same
            // key was reused with a different body. Refresh the balance + ask
            // the user to retry with a fresh key.
            setIdempotencyKey(crypto.randomUUID());
            setStatus("error");
            return;
          }
          setStatus("error");
        },
      },
    );
  }

  function handleTopUpSuccess() {
    // Top-up modal closed itself; re-open the confirm modal with the freshly
    // credited balance + mint a new idempotency key for the retry.
    setIdempotencyKey(crypto.randomUUID());
    setStatus("idle");
    setConfirmOpen(true);
  }

  function handleViewVault() {
    // Epic 4 vault doesn't exist yet — link to wallet as the closest
    // post-purchase landing surface (the debit is visible in history).
    navigate("/account/wallet");
  }

  function handleBackToShop() {
    // The deep-link source is the Astro public-site; the SPA can't know
    // where exactly to go, so use browser history back (-1) when possible.
    if (window.history.length > 1) {
      navigate(-1);
    } else {
      navigate("/");
    }
  }

  function handleConfirmOpenChange(next: boolean) {
    setConfirmOpen(next);
    if (!next && status === "idle") {
      // User closed without confirming — go back.
      handleBackToShop();
    }
  }

  if (drop.isPending || balance.isPending) {
    return (
      <main className="min-h-screen p-8">
        <p className="text-sm text-muted-foreground">Loading drop…</p>
      </main>
    );
  }

  if (drop.error || !drop.data) {
    return (
      <main className="min-h-screen p-8 space-y-4">
        <h1 className="text-2xl font-semibold">Drop not found</h1>
        <p className="text-sm text-muted-foreground">
          We couldn't find this dossier drop. It may have retired or been
          removed.
        </p>
        <button
          type="button"
          onClick={handleBackToShop}
          className="text-sm underline decoration-dotted"
        >
          ← Back to shop
        </button>
      </main>
    );
  }

  return (
    <main className="min-h-screen p-8">
      <header className="mb-6 max-w-md">
        <button
          type="button"
          onClick={handleBackToShop}
          className="text-sm underline decoration-dotted opacity-70 hover:opacity-100"
        >
          ← Back to shop
        </button>
        <h1 className="mt-3 text-2xl font-semibold">
          Confirm your dossier purchase
        </h1>
      </header>

      <PurchaseConfirmModal
        open={confirmOpen}
        onOpenChange={handleConfirmOpenChange}
        itemTitle={drop.data.portfolio_title}
        itemSubtitle={itemSubtitle}
        priceCents={priceCents}
        balanceCents={balanceCents}
        onConfirm={handleConfirm}
        onTopUp={() => {
          setConfirmOpen(false);
          setTopupOpen(true);
        }}
        insufficientShortfallCents={status === "idle" ? shortfallCents : undefined}
        status={status}
        errorMessage={lastError?.message}
        quantity={quantity}
        onQuantityChange={setQuantity}
        maxQuantity={maxQuantity}
        successDossierEdition={purchaseResult?.edition_number}
        successEditions={purchaseResult?.dossiers.map((d) => d.edition_number)}
        capCount={lastError?.user_owned_count}
        capLimit={lastError?.per_user_cap}
        dropCurrentStatus={lastError?.current_status ?? drop.data.status}
        dropId={dropId}
        onViewVault={handleViewVault}
        onBackToShop={handleBackToShop}
      />

      <TopUpModal
        open={topupOpen}
        onOpenChange={setTopupOpen}
        onSuccess={handleTopUpSuccess}
      />
    </main>
  );
}
